/**
 * Sibling hook to `useEdgeMonitorSignalR` — polls the EDGE-MONITOR-V2 Phase A
 * admin endpoints for the selected machine. Cadence per spec §3.4:
 *
 *   • EdgeAgentHub conn pill ........... 5 s
 *   • Catalog tile ..................... 30 s
 *   • Audit log ........................ manual + 10 s while auto-refresh on
 *   • SiteAgent detail ................. follows the existing 30 s poll
 *
 * No SignalR push for any of these (spec §3.4) — admin data is low-churn and
 * polling is cheap enough.
 *
 * State lives in `state.machineGateway` so multiple components can read the
 * same snapshot without re-fetching. Stale-while-revalidate per
 * CONNECTIVITY_BACKEND_DEV_PROMPT.txt — keeps prior data on refresh, only
 * shows loading when there is no existing data.
 */
import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "../../../core/store";
import {
  fetchBotAuditLog,
  fetchEdgeHubStatus,
  fetchSiteAgentDetail,
  fetchSymbolCatalogStatus,
  selectBotAuditLogEntry,
  selectEdgeHubStatusEntry,
  selectSiteAgentDetailEntry,
  selectSymbolCatalogStatusEntry,
} from "../machineGatewaySlice";

export interface UseEdgeMonitorAdminArgs {
  machineId: string | null;
  /**
   * Optional LIST-derived fallback agent id. The hook prefers the
   * authoritative `siteAgentId` returned by its own internally-fetched
   * edge-hub-status entry and only falls back to this value before the first
   * edge-hub-status response lands. Passing it here (instead of pre-resolving
   * the preference at the call site) avoids a temporal-dead-zone reference to
   * the hook's own return value.
   */
  fallbackSiteAgentId?: string | null;
  /** When true, audit log polls every 10s; otherwise manual via `refreshAuditLog`. */
  autoRefreshAuditLog?: boolean;
  /** Cap on audit log rows (server clamps to 200; pass through unchanged). */
  auditLogLimit?: number;
}

const EDGE_HUB_POLL_MS = 5_000;
const CATALOG_POLL_MS = 30_000;
const AUDIT_LOG_POLL_MS = 10_000;
const SITE_AGENT_POLL_MS = 30_000;

export function useEdgeMonitorAdmin(args: UseEdgeMonitorAdminArgs) {
  const {
    machineId,
    fallbackSiteAgentId,
    autoRefreshAuditLog = false,
    auditLogLimit = 50,
  } = args;

  const dispatch = useDispatch<AppDispatch>();

  // Selectors — return undefined when never fetched, which the components
  // distinguish from `notAvailable=true` (cross-tenant / 403 / explicit miss).
  const edgeHubEntry = useSelector((s: RootState) =>
    machineId ? selectEdgeHubStatusEntry(s, machineId) : undefined
  );
  const catalogEntry = useSelector((s: RootState) =>
    machineId ? selectSymbolCatalogStatusEntry(s, machineId) : undefined
  );
  const auditLogEntry = useSelector((s: RootState) =>
    machineId ? selectBotAuditLogEntry(s, machineId) : undefined
  );

  // Prefer the siteAgentId resolved by the edge-hub-status endpoint (it maps
  // machine→agent authoritatively); fall back to the caller-supplied
  // LIST-derived id so the per-agent detail tile keeps loading even before the
  // first edge-hub-status response lands. Resolving the preference here (rather
  // than at the call site) avoids referencing the hook's own return value
  // before it is initialised.
  const siteAgentId = edgeHubEntry?.data?.siteAgentId ?? fallbackSiteAgentId ?? null;

  const siteAgentEntry = useSelector((s: RootState) =>
    siteAgentId ? selectSiteAgentDetailEntry(s, siteAgentId) : undefined
  );

  // Manual triggers exposed to components (refresh button on the audit table,
  // explicit re-poll on user action, etc).
  const refreshEdgeHubStatus = useCallback(() => {
    if (machineId) {
      dispatch(fetchEdgeHubStatus(machineId));
    }
  }, [dispatch, machineId]);

  const refreshSymbolCatalogStatus = useCallback(() => {
    if (machineId) {
      dispatch(fetchSymbolCatalogStatus(machineId));
    }
  }, [dispatch, machineId]);

  const refreshAuditLog = useCallback(() => {
    if (machineId) {
      dispatch(fetchBotAuditLog({ machineId, limit: auditLogLimit }));
    }
  }, [dispatch, machineId, auditLogLimit]);

  const refreshSiteAgentDetail = useCallback(() => {
    if (siteAgentId) {
      dispatch(fetchSiteAgentDetail(siteAgentId));
    }
  }, [dispatch, siteAgentId]);

  // ----- Edge hub status — 5 s
  useEffect(() => {
    if (!machineId) return;
    refreshEdgeHubStatus();
    const id = window.setInterval(refreshEdgeHubStatus, EDGE_HUB_POLL_MS);
    return () => window.clearInterval(id);
  }, [machineId, refreshEdgeHubStatus]);

  // ----- Symbol catalog status — 30 s
  useEffect(() => {
    if (!machineId) return;
    refreshSymbolCatalogStatus();
    const id = window.setInterval(refreshSymbolCatalogStatus, CATALOG_POLL_MS);
    return () => window.clearInterval(id);
  }, [machineId, refreshSymbolCatalogStatus]);

  // ----- Audit log — manual + optional 10 s auto-refresh
  useEffect(() => {
    if (!machineId) return;
    refreshAuditLog();
    if (!autoRefreshAuditLog) return;
    const id = window.setInterval(refreshAuditLog, AUDIT_LOG_POLL_MS);
    return () => window.clearInterval(id);
  }, [machineId, autoRefreshAuditLog, refreshAuditLog]);

  // ----- SiteAgent detail — 30 s when an id is known
  useEffect(() => {
    if (!siteAgentId) return;
    refreshSiteAgentDetail();
    const id = window.setInterval(refreshSiteAgentDetail, SITE_AGENT_POLL_MS);
    return () => window.clearInterval(id);
  }, [siteAgentId, refreshSiteAgentDetail]);

  return {
    edgeHubEntry,
    catalogEntry,
    auditLogEntry,
    siteAgentEntry,
    refreshEdgeHubStatus,
    refreshSymbolCatalogStatus,
    refreshAuditLog,
    refreshSiteAgentDetail,
  };
}
